import { InvoiceItemBase } from '../../invoicing/abstract/invoice-item-base.model';
import { ItemType } from '../item-type.model';
import { ItemTypeEnum } from 'src/app/@shared/enums/item-type.enum';
import { OmitField } from 'src/app/@shared/decorators/api-field.decorator';
import { TaxExemptionSourceEnum } from 'src/app/@shared/enums/tax-exemption-source.enum';

export class SalesInvoiceItemBase extends InvoiceItemBase {

    static override CLASS_NAME: string = 'SalesInvoiceItemBase';

    private _ecoTaxIncluded!: boolean;
    private _ecoTaxGrossAmount!: number;
    private _ecoTaxNetAmount!: number;
    private _itemType!: ItemType;
    private _option!: boolean;
    private _progression!: number;
    private _quotationItemProgression!: number;
    private _quotationItemPreviousProgression!: number;
    private _taxExemptionSource!: TaxExemptionSourceEnum | null;

    public get ecoTaxIncluded(): boolean {
        return this._ecoTaxIncluded;
    }

    public set ecoTaxIncluded(value: any) {
        this._setValue('_ecoTaxIncluded', value, 'boolean');
    }

    public get ecoTaxGrossAmount(): number {
        return this._ecoTaxGrossAmount;
    }

    public set ecoTaxGrossAmount(value: any) {
        this._setValue('_ecoTaxGrossAmount', value, 'float');
    }

    public get ecoTaxNetAmount(): number {
        return this._ecoTaxNetAmount;
    }

    public set ecoTaxNetAmount(value: any) {
        this._setValue('_ecoTaxNetAmount', value, 'float');
    }

    public get itemType(): ItemType {
        return this._itemType;
    }

    public set itemType(value: any) {
        this._setValue('_itemType', value, 'ItemType');
    }

    public get isProduct(): boolean {
        return this.itemType?.code === ItemTypeEnum.Product;
    }

    public get isSection(): boolean {
        return this.itemType?.code === ItemTypeEnum.Section;
    }

    public get isSubSection(): boolean {
        return this.itemType?.code === ItemTypeEnum.SubSection;
    }

    public get option(): boolean {
        return this._option;
    }

    public set option(value: boolean) {
        this._setValue('_option', value, 'boolean');
    }

    public get progression(): number {
        return this._progression;
    }

    public set progression(value: number) {
        this._setValue('_progression', value, 'float');
    }

    public get quotationItemProgression(): number {
        return this._quotationItemProgression;
    }

    public set quotationItemProgression(value: number) {
        this._setValue('_quotationItemProgression', value, 'float', {precision: 5});
    }

    public get quotationItemPreviousProgression(): number {
        return this._quotationItemPreviousProgression;
    }

    public set quotationItemPreviousProgression(value: number) {
        this._setValue('_quotationItemPreviousProgression', value, 'float', {precision: 5});
    }

    /**
     * Ce qui rend **cette ligne** non taxable, ou `null` si rien ne l'impose — #1089.
     *
     * **Calculé et servi par le serveur**, jamais renvoyé (`@OmitField`), selon son ordre
     * de résolution : territoire → produit → tiers. C'est le même ordre qui choisit le
     * motif pré-alimenté, et c'est pourquoi la cause ne se recalcule pas ici : sur une
     * vente d'un bien de première nécessité à un client par ailleurs exonéré, le serveur
     * répond `product` et propose le motif 25 — un calcul local qui ferait primer la
     * fiche du tiers annoncerait « votre client est exonéré » au-dessus de « biens de
     * première nécessité ».
     *
     * Verrou de la case « Exonéré ? » ⇔ valeur non nulle, les trois causes comprises :
     * la taxe d'une ligne n'étant pas choisissable, décocher pose `tax = null` et le
     * serveur la re-dérive du catalogue — sur un groupe à 0 %, l'aller-retour ramène
     * 0 %. `null` désigne au contraire l'exonération cochée à la main, la seule que
     * l'utilisateur puisse annuler.
     *
     * ⚠️ Absent d'une ligne que le serveur n'a jamais vue : cf.
     * `InvoiceItemFormComponent.taxExemptionSource`, qui retombe alors sur le document.
     */
    @OmitField()
    public get taxExemptionSource(): TaxExemptionSourceEnum | null {
        return this._taxExemptionSource;
    }

    public set taxExemptionSource(value: any) {
        this._setValue('_taxExemptionSource', value, 'string');
    }

}
